// useContractPerformance — stitches deliverables / milestones / KPIs +
// KPI measurements into a ContractPerformanceSnapshot and runs
// computeContractPerformance. Reuses useAllDeliverablesForSummary so
// the deliverable summary-strip cache is shared. KPI measurements are
// fetched contract-wide once and bucketed client-side by parentId so
// the gauge can compute each KPI's latest RAG without an N+1 query.
// Returns isPending + the result; consumers render a muted gauge until
// data arrives. Falls back to all-insufficient when any underlying
// query is still pending. A failed underlying query surfaces as `error`
// (isPending false) so the consumer can render an unavailable state
// instead of a permanently-pending gauge.

import { useMemo } from "react";
import { useAllDeliverablesForSummary } from "./useTracking";
import { useFlatTrackingList } from "./useFlatTrackingList";
import {
  computeContractPerformance,
  type ContractPerformanceResult,
  type ContractPerformanceSnapshot,
  type DeliverableSummaryInput,
} from "../lib/contract-overview/contractPerformance";
import { calculateMilestonesOnTime } from "../components/manage/tracking/milestone/calculateMilestonesOnTime";
import { getBrisbaneToday } from "../utils/getBrisbaneToday";
import { computeAutoRag } from "../lib/kpi/autoRag";
import type { Deliverable, Meeting, Kpi, KpiMeasurement, Milestone } from "../types";

/** Project a list of Deliverable records to the minimal shape the grade
 *  pillar consumes. Archived deliverables are filtered out so the FE
 *  pillar matches the BE register pillar — the BE DDB query already
 *  filters `attribute_not_exists(#archivedAt)`. Exported (rather than
 *  inlined in the hook) so the parity behaviour is unit-testable without
 *  standing up a 5-endpoint MSW harness for the hook itself. */
export function buildDeliverablePillarInputs(
  deliverables: ReadonlyArray<Deliverable>,
): DeliverableSummaryInput[] {
  return deliverables
    .filter((d) => !(d as { archivedAt?: string | null }).archivedAt)
    .map((d) => ({
      id: d.id,
      dueDate: d.dueDate,
      submittedAt: (d as { submittedAt?: string | null }).submittedAt ?? null,
      recurrenceRule: d.recurrenceRule,
      parentId: d.parentId,
    }));
}

const EMPTY: ContractPerformanceResult = {
  deliverables: {
    onTimePct: null,
    sampleSize: 0,
    threshold: "insufficient",
    smallSample: false,
  },
  milestones: {
    onTimePct: null,
    sampleSize: 0,
    threshold: "insufficient",
    smallSample: false,
  },
  kpis: {
    onTimePct: null,
    onTrackPct: null,
    sampleSize: 0,
    threshold: "insufficient",
    smallSample: false,
  },
  overallLabel: "Insufficient data",
  reportingPillarCount: 0,
  pillarsTrackingInSuccess: 0,
};

interface UseContractPerformanceResult {
  result: ContractPerformanceResult;
  isPending: boolean;
  /** First failure among the underlying tracking queries. A failed query
   *  must surface as an error, never as a permanently-pending gauge. */
  error: Error | null;
}

function pickLatestRag(
  kpi: Kpi,
  measurementsByParent: Map<string, KpiMeasurement[]>,
): "green" | "amber" | "red" | null {
  // Pick the measurement with the highest period key — period is an
  // ISO-prefixed reporting label (`2026-04`, `2026-Q2`), so a
  // localeCompare descending sort surfaces the latest reading.
  const measurements = measurementsByParent.get(kpi.id) ?? [];
  if (measurements.length === 0) return null;
  const latest = measurements
    .slice()
    .sort((a, b) => b.period.localeCompare(a.period))[0];
  if (!latest.value || latest.value.trim().length === 0) return null;
  return computeAutoRag(latest.value, kpi);
}

export interface UseContractPerformanceOptions {
  /** Per-contract success threshold (1–100). Falls back to the default
   *  in `contractPerformance.ts` (90) when undefined/null. */
  successThreshold?: number | null;
  /** Per-contract watch threshold (1–100). Falls back to the default
   *  in `contractPerformance.ts` (70) when undefined/null. */
  watchThreshold?: number | null;
}

export function useContractPerformance(
  contractId: string,
  options?: UseContractPerformanceOptions,
): UseContractPerformanceResult {
  const deliverablesQ = useAllDeliverablesForSummary(contractId);
  const milestonesQ = useFlatTrackingList<Milestone>(contractId, "milestone");
  const meetingsQ = useFlatTrackingList<Meeting>(contractId, "meeting");
  const kpisQ = useFlatTrackingList<Kpi>(contractId, "kpi");
  // Single contract-wide query for KPI measurements — the same approach
  // useTrackingItemsByParent uses internally — then bucket client-side
  // by parentId so the gauge picks the latest reading per KPI without
  // an N+1 fetch.
  const measurementsQ = useFlatTrackingList<KpiMeasurement>(
    contractId,
    "kpi-measurement",
  );

  const isPending =
    deliverablesQ.isPending ||
    milestonesQ.isPending ||
    meetingsQ.isPending ||
    kpisQ.isPending ||
    measurementsQ.isPending;

  // useAllDeliverablesForSummary types its error as unknown — normalise so
  // the result shape always carries Error | null.
  const rawError =
    deliverablesQ.error ??
    milestonesQ.error ??
    meetingsQ.error ??
    kpisQ.error ??
    measurementsQ.error ??
    null;
  const error = useMemo<Error | null>(
    () =>
      rawError == null
        ? null
        : rawError instanceof Error
          ? rawError
          : new Error(String(rawError)),
    [rawError],
  );

  const successThreshold = options?.successThreshold ?? null;
  const watchThreshold = options?.watchThreshold ?? null;

  return useMemo<UseContractPerformanceResult>(() => {
    if (error) {
      return { result: EMPTY, isPending: false, error };
    }
    if (
      isPending ||
      !milestonesQ.data ||
      !meetingsQ.data ||
      !kpisQ.data ||
      !measurementsQ.data
    ) {
      return { result: EMPTY, isPending: true, error: null };
    }

    const measurementsByParent = new Map<string, KpiMeasurement[]>();
    for (const m of measurementsQ.data) {
      if (!m.parentId) continue;
      const bucket = measurementsByParent.get(m.parentId);
      if (bucket) bucket.push(m);
      else measurementsByParent.set(m.parentId, [m]);
    }

    // Milestones on-time uses a milestone-specific calculator: a milestone
    // that is past due AND undelivered counts as not-on-time (the meeting
    // calculator excludes such records, which would mask slippage on the
    // performance gauge). Future-due undelivered milestones are not yet
    // measurable and are excluded from the sample.
    const milestoneRate = calculateMilestonesOnTime(
      milestonesQ.data,
      getBrisbaneToday(),
    );

    const snapshot: ContractPerformanceSnapshot = {
      deliverables: buildDeliverablePillarInputs(deliverablesQ.data),
      milestonesOnTimeRate: milestoneRate,
      kpis: kpisQ.data.map((k) => ({
        id: k.id,
        latestRag: pickLatestRag(k, measurementsByParent),
      })),
    };

    return {
      result: computeContractPerformance(snapshot, {
        successThreshold,
        watchThreshold,
      }),
      isPending: false,
      error: null,
    };
  }, [
    isPending,
    error,
    deliverablesQ.data,
    milestonesQ.data,
    meetingsQ.data,
    kpisQ.data,
    measurementsQ.data,
    successThreshold,
    watchThreshold,
  ]);
}
